import {Component, DOCUMENT, HostListener, inject, OnInit, Renderer2, ChangeDetectionStrategy} from '@angular/core';
import {AuthService} from "../../services/auth.service";
import {environment} from "../../../environments/environment";
import {IdleWatcherService} from "../../services/idle-watcher.service";
import { WeaveLoadingOverlayComponent } from "../../shared/loaders/weave-loading-overlay.component";
import { RouterOutlet } from '@angular/router';
import { PendoService } from "../../services/pendo.service";
import { BuildVersionService } from "../../services/build-version.service";
import { StaleBundleBannerComponent } from "../shared/stale-bundle-banner/stale-bundle-banner.component";

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [WeaveLoadingOverlayComponent, RouterOutlet, StaleBundleBannerComponent]
})
export class AppComponent implements OnInit {
  private document = inject(DOCUMENT);
  private authService: AuthService = inject(AuthService);
  private renderer: Renderer2 = inject(Renderer2);
  private readonly idleService = inject(IdleWatcherService);
  private readonly pendoService = inject(PendoService);
  // WAM-602 — app-wide stale-bundle detection, not gated on auth: an
  // unauthenticated tab left open on the login page is just as stale.
  private readonly buildVersion = inject(BuildVersionService);

  // The `keyboardNav` body class that used to be toggled here (added on a Tab keyup, removed
  // on any document click) is gone. It existed to gate the focus ring on "is this person
  // using the keyboard", and got that wrong in both directions: anything focused by SCRIPT
  // was ringed as though tabbed to, while pressing Enter or Space on a control fired a
  // `click` that bubbled here and STRIPPED the class — removing the ring in exactly the case
  // it exists for. `sass/accessibility.scss` now uses `*:focus-visible`, which is the
  // browser's own answer to the same question and needs no listener at all.
  //
  // Single app-wide handler for every "skip" link (a.skip-main). A native
  // href="#target" anchor resolves against <base href="/"> and navigates to the
  // root route (redirected to home), so intercept it here and instead move focus
  // to the in-page target without touching the URL or history.
  @HostListener('document:click', ['$event'])
  handleSkipLink(event: MouseEvent) {
    const link = (event.target as HTMLElement)?.closest('a.skip-main');
    if (!link) return;
    const href = link.getAttribute('href') ?? '';
    if (!href.startsWith('#')) return;
    event.preventDefault();
    const target = this.document.getElementById(href.slice(1));
    if (!target) return;
    if (!target.hasAttribute('tabindex')) {
      this.renderer.setAttribute(target, 'tabindex', '-1');
    }
    target.focus();
    target.scrollIntoView();
  }

  ngOnInit() {
    const observerUserAuthentication = this.authService.getAuthObservable();
    observerUserAuthentication.subscribe(authenticated => {
      if(authenticated) {
        this.pendoService.initializePendoService();
        this.idleService.watch();
        this.loadZendeskWidget();
      } else {
        this.removeZendeskWidget();
      }
    });
    this.addPreconnectLink();
    this.buildVersion.start();
  }
  detectBrowser(){
    const agent = window.navigator.userAgent.toLowerCase()
    switch (true) {
      case agent.indexOf('edge') > -1:
        return 'edge';
      case agent.indexOf('opr') > -1 && !!(<any>window).opr:
        return 'opera';
      case agent.indexOf('chrome') > -1 && !!(<any>window).chrome:
        return 'chrome';
      case agent.indexOf('trident') > -1:
        return 'ie';
      case agent.indexOf('firefox') > -1:
        return 'firefox';
      case agent.indexOf('safari') > -1:
        return 'safari';
      default:
        return 'other';
    }

  }

  private addPreconnectLink(): void {
    const imageOrigin = environment.s3Bucket;
    if (!imageOrigin) return;

    const link = this.document.createElement('link');
    link.rel = 'preconnect';
    link.href = imageOrigin;
    this.document.head.appendChild(link);
  }

  private loadZendeskWidget(): void {
    // Check if widget is already loaded
    if (this.document.getElementById('ze-snippet')) {
      return;
    }

    const script = this.document.createElement('script');
    script.id = 'ze-snippet';
    script.src = 'https://static.zdassets.com/ekr/snippet.js?key=5339a62c-63e4-42ae-aa6d-68d2e7512af6';
    script.async = true;
    this.document.head.appendChild(script);
  }

  private removeZendeskWidget(): void {
    // Remove the Zendesk script
    const script = this.document.getElementById('ze-snippet');
    if (script) {
      script.remove();
    }

    // Hide the Zendesk widget if it's already loaded
    if (typeof (window as any).zE !== 'undefined') {
      (window as any).zE('messenger', 'hide');
    }
  }

}
